import googleSheetsService from '../services/googleSheets';
import { TransactionalPricingSlab } from '../types';

export interface TransactionalPricingResult {
  totalEmails: number;
  monthlyEmailPrice: number;
  discountPercentage: number;
  discountAmount: number;
  discountedEmailPrice: number;
  dedicatedIpPrice: number;
  totalMonthlyPrice: number;
  currency: 'INR' | 'USD';
  slab: TransactionalPricingSlab;
  dedicatedIpCount: number;
  pricePerIp: number;
}

export interface TransactionalQuoteData {
  quoteNumber: string;
  quoteDate: string;
  customerName: string;
  companyName: string;
  emailVolume: number;
  pricing: TransactionalPricingResult;
  discountReason?: string;
  generatedBy?: {
    name: string;
    email: string;
  };
}

// Cache for pricing slabs to avoid repeated API calls
let cachedTransactionalPricingSlabs: {
  INR: TransactionalPricingSlab[] | null;
  USD: TransactionalPricingSlab[] | null;
  lastFetch: number;
} = {
  INR: null,
  USD: null,
  lastFetch: 0
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Fetch transactional pricing slabs from Google Sheets
export const getTransactionalPricingSlabs = async (currency: 'INR' | 'USD'): Promise<TransactionalPricingSlab[]> => {
  const now = Date.now();
  
  // Check cache
  if (cachedTransactionalPricingSlabs[currency] && (now - cachedTransactionalPricingSlabs.lastFetch) < CACHE_DURATION) {
    return cachedTransactionalPricingSlabs[currency]!;
  }
  
  try {
    const slabs = await googleSheetsService.getTransactionalPricingSlabsByCurrency(currency);
    cachedTransactionalPricingSlabs[currency] = slabs;
    cachedTransactionalPricingSlabs.lastFetch = now;
    return slabs;
  } catch (error) {
    console.error('Failed to fetch transactional pricing slabs:', error);
    // Return fallback pricing if Google Sheets fails
    return getFallbackTransactionalPricingSlabs(currency);
  }
};

// Fallback pricing data in case Google Sheets is unavailable
const getFallbackTransactionalPricingSlabs = (currency: 'INR' | 'USD'): TransactionalPricingSlab[] => {
  const now = new Date().toISOString();
  
  if (currency === 'USD') {
    return [
      {
        slabId: 'trans_usd_standard',
        currency: 'USD',
        minEmails: 0,
        maxEmails: 1500000,
        pricePerThousand: 0.20,
        label: 'Standard (up to 1.5M)',
        isActive: true,
        dedicatedIpPrice: 50,
        createdDate: now,
        updatedDate: now,
      },
      {
        slabId: 'trans_usd_high_volume',
        currency: 'USD',
        minEmails: 1500001,
        maxEmails: null,
        pricePerThousand: 0.18,
        label: 'High Volume (above 1.5M)',
        isActive: true,
        dedicatedIpPrice: 50,
        createdDate: now,
        updatedDate: now,
      },
    ];
  } else {
    // INR pricing (1 USD = 83 INR approximation)
    return [
      {
        slabId: 'trans_inr_standard',
        currency: 'INR',
        minEmails: 0,
        maxEmails: 1500000,
        pricePerThousand: 16.60,
        label: 'Standard (up to 1.5M)',
        isActive: true,
        dedicatedIpPrice: 4150,
        createdDate: now,
        updatedDate: now,
      },
      {
        slabId: 'trans_inr_high_volume',
        currency: 'INR',
        minEmails: 1500001,
        maxEmails: null,
        pricePerThousand: 14.94,
        label: 'High Volume (above 1.5M)',
        isActive: true,
        dedicatedIpPrice: 4150,
        createdDate: now,
        updatedDate: now,
      },
    ];
  }
};

// Clear cache when needed
export const clearTransactionalPricingCache = () => {
  cachedTransactionalPricingSlabs = {
    INR: null,
    USD: null,
    lastFetch: 0
  };
};

export const calculateTransactionalPricing = async (
  totalEmails: number, 
  currency: 'INR' | 'USD' = 'USD',
  dedicatedIpCount: number = 0,
  discountPercentage: number = 0
): Promise<TransactionalPricingResult> => {
  let slabs = await getTransactionalPricingSlabs(currency);
  
  // If no slabs from Google Sheets, use fallback data
  if (slabs.length === 0) {
    console.log('Using fallback transactional pricing data');
    slabs = getFallbackTransactionalPricingSlabs(currency);
  }

  if (totalEmails <= 0) {
    return {
      totalEmails: 0,
      monthlyEmailPrice: 0,
      discountPercentage: 0,
      discountAmount: 0,
      discountedEmailPrice: 0,
      dedicatedIpPrice: 0,
      totalMonthlyPrice: 0,
      currency,
      slab: slabs[0],
      dedicatedIpCount: 0,
      pricePerIp: slabs[0]?.dedicatedIpPrice || 0,
    };
  }

  // Find the matching slab for the total email volume
  let matchingSlab: TransactionalPricingSlab | null = null;
  
  for (const slab of slabs) {
    const maxEmails = slab.maxEmails || Infinity;
    
    if (totalEmails >= slab.minEmails && totalEmails <= maxEmails) {
      matchingSlab = slab;
      break;
    }
  }

  // Fallback to the highest slab if no match found
  if (!matchingSlab) {
    matchingSlab = slabs[slabs.length - 1];
  }

  // Validate discount percentage (0-50%)
  const validDiscountPercentage = Math.max(0, Math.min(50, discountPercentage));

  // Calculate monthly email price: (emails / 1000) × price per thousand
  const emailsInThousands = totalEmails / 1000;
  const monthlyEmailPrice = emailsInThousands * matchingSlab.pricePerThousand;
  
  // Calculate discount (only on email costs, not on dedicated IP)
  const discountAmount = (monthlyEmailPrice * validDiscountPercentage) / 100;
  const discountedEmailPrice = monthlyEmailPrice - discountAmount;
  
  // Calculate dedicated IP price based on count (no discount applied)
  const pricePerIp = matchingSlab.dedicatedIpPrice;
  const dedicatedIpPrice = dedicatedIpCount * pricePerIp;
  
  // Calculate total with discount applied only to email costs
  const totalMonthlyPrice = discountedEmailPrice + dedicatedIpPrice;
  
  return {
    totalEmails,
    monthlyEmailPrice: Math.round(monthlyEmailPrice * 100) / 100, // Round to 2 decimals
    discountPercentage: validDiscountPercentage,
    discountAmount: Math.round(discountAmount * 100) / 100, // Round to 2 decimals
    discountedEmailPrice: Math.round(discountedEmailPrice * 100) / 100, // Round to 2 decimals
    dedicatedIpPrice: Math.round(dedicatedIpPrice * 100) / 100, // Round to 2 decimals
    totalMonthlyPrice: Math.round(totalMonthlyPrice * 100) / 100, // Round to 2 decimals
    currency,
    slab: matchingSlab,
    dedicatedIpCount: Math.max(0, dedicatedIpCount), // Ensure non-negative
    pricePerIp,
  };
};

export const formatTransactionalCurrency = (amount: number, currency: 'INR' | 'USD'): string => {
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return formatter.format(amount);
};

export const formatTransactionalEmailCount = (count: number): string => {
  if (count >= 10000000) { // 1 crore and above
    const crores = count / 10000000;
    return crores >= 1 ? `${crores.toFixed(crores >= 10 ? 0 : 1)} cr` : `${(count / 100000).toFixed(0)} lakh`;
  } else if (count >= 100000) { // 1 lakh and above
    const lakhs = count / 100000;
    return `${lakhs.toFixed(lakhs >= 10 ? 0 : 1)} lakh`;
  } else if (count >= 1000) { // 1K and above
    return `${(count / 1000).toFixed(0)}K`;
  }
  return count.toString();
};

export const generateTransactionalQuoteNumber = (): string => {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, ''); // YYYYMMDD
  const timeStr = now.getHours().toString().padStart(2, '0') + now.getMinutes().toString().padStart(2, '0');
  const randomStr = Math.random().toString(36).substr(2, 3).toUpperCase();
  return `TQ-${dateStr}-${timeStr}-${randomStr}`; // TQ for Transactional Quote
};

export const createTransactionalQuoteData = (
  customerName: string,
  companyName: string,
  emailVolume: number,
  pricing: TransactionalPricingResult,
  discountReason?: string,
  generatedBy?: { name: string; email: string }
): TransactionalQuoteData => {
  return {
    quoteNumber: generateTransactionalQuoteNumber(),
    quoteDate: new Date().toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }),
    customerName,
    companyName,
    emailVolume,
    pricing,
    discountReason,
    generatedBy
  };
};